import { FieldType, ViewModelBase, isEmptyOrWhitespace, isNullOrUndefined } from "@shoothill/core";
import { action, computed, observable } from "mobx";

import { AppUrls } from "AppUrls";
import { StockTransactionRequestStatusType } from "Globals/Models/Enums/StockTransactionRequestStatusType";
import { ServerViewModel } from "Globals/ViewModels/ServerViewModel";
import { formatCurrencyFromPounds, formatDate, formatTime } from "Utils/Format";
import { StockTransferApprovalModel } from "./StockTransferApprovalModel";

export class StockTransferApprovalViewModel extends ViewModelBase<StockTransferApprovalModel> {
    public server: ServerViewModel = new ServerViewModel();

    public canApproveAndReject: boolean;
    private parentCloseAction: (refreshPage: boolean) => void;

    constructor(canApproveAndReject: boolean, closeAction: (refreshPage: boolean) => void) {
        super(new StockTransferApprovalModel(), false);

        this.canApproveAndReject = canApproveAndReject;
        this.parentCloseAction = closeAction;

        this.setDecorators(StockTransferApprovalModel);
    }

    // #region Properties

    @computed
    public get displayName() {
        const displayName = "Stock transfer";

        if (isNullOrUndefined(this.model.currentTransactionRequestStatusType)) {
            return displayName;
        }

        return `${displayName} (${this.model.currentTransactionRequestStatusType?.displayName})`;
    }

    @computed
    public get costOfTransfer() {
        if (!isNullOrUndefined(this.model.costOfTransfer)) {
            return formatCurrencyFromPounds(this.model.costOfTransfer!);
        } else {
            return "-";
        }
    }

    @computed
    public get canDisplayCostOfTransfer() {
        return !isNullOrUndefined(this.model.costOfTransfer);
    }

    @computed
    public get createdDate() {
        return `${formatDate(this.model.createdDate)} @ ${formatTime(this.model.createdDate)}`;
    }

    @computed
    public get lastUpdatedDate() {
        return isEmptyOrWhitespace(this.model.lastUpdatedDate) ? "" : `${formatDate(this.model.lastUpdatedDate!)} @ ${formatTime(this.model.lastUpdatedDate!)}`;
    }

    @computed
    public get canDisplayLastUpdated() {
        return !isEmptyOrWhitespace(this.model.lastUpdatedDate) && !isEmptyOrWhitespace(this.model.lastUpdatedByUser);
    }

    @computed
    public get canApprove() {
        return this.canApproveAndReject && this.model.availableTransactionRequestStatusTypes.some((st) => st.IsOfType(StockTransactionRequestStatusType.Approved));
    }

    @computed
    public get canReject() {
        return this.canApproveAndReject && this.model.availableTransactionRequestStatusTypes.some((st) => st.IsOfType(StockTransactionRequestStatusType.Rejected));
    }

    @computed
    public get canCancel() {
        return this.canApproveAndReject && this.model.availableTransactionRequestStatusTypes.length > 0;
    }

    // #endregion Properties

    // #region Actions

    @action
    public approve = () => {
        const statusType = this.model.availableTransactionRequestStatusTypes.find((st) => st.IsOfType(StockTransactionRequestStatusType.Approved));

        if (!isNullOrUndefined(statusType)) {
            this.model.transactionRequestStatusType = statusType!;
            this.apiSaveDetailsAsync();
        }
    };

    @action
    public reject = () => {
        const statusType = this.model.availableTransactionRequestStatusTypes.find((st) => st.IsOfType(StockTransactionRequestStatusType.Rejected));

        if (!isNullOrUndefined(statusType)) {
            this.model.transactionRequestStatusType = statusType!;
            this.apiSaveDetailsAsync();
        }
    };

    @action
    public cancel = () => this.parentCloseAction(false);

    // #endregion Actions

    // #region Api Actions

    @action
    public apiSaveDetailsAsync = async (): Promise<void> => {
        await this.server.command<any>(
            () => this.Post(AppUrls.Server.Stock.TransferStock.replace("{stocktransactionrequestid}", this.model.transactionRequestId!), this.model.toDto()),
            (result) => this.parentCloseAction(true),
            this.isModelValid,
            "Error whilst saving the stock approval",
        );

        if (this.server.HaveValidationMessage) {
            this.setSnackMessage(this.server.ValidationMessage);
            this.setSnackType(this.SNACKERROR);
            this.setSnackbarState(true);
        }
    };

    // #endregion Api Actions

    // #region Snack Bar

    public SNACKERROR = "error";

    @observable
    public snackbarState = false;

    @observable
    public snackType = "";

    @action
    public setSnackbarState = (val: boolean) => {
        this.snackbarState = val;
    };

    @observable
    public snackMessage = "";

    @action
    public setSnackMessage = (val: string) => {
        this.snackMessage = val;
    };

    @action
    public setSnackType = (val: string) => {
        this.snackType = val;
    };

    // #endregion Snack Bar

    // #region Boilerplate

    public async isFieldValid(fieldName: keyof FieldType<StockTransferApprovalModel>): Promise<boolean> {
        const { isValid, errorMessage } = await this.validateDecorators(fieldName);

        this.setError(fieldName, errorMessage);
        this.setValid(fieldName, isValid);

        return isValid;
    }

    public afterUpdate: undefined;
    public beforeUpdate: undefined;

    // #endregion Boliderplate
}
